import { safeStorage } from '@utils/storage'
import { create, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { createStore } from 'zustand/vanilla'
import {
  type AnswersState,
  type AnswersStore,
  answerActions,
  answersStorageKey,
  consultationOwner,
  createAnswersStore,
  emptyAnswers,
  forgetOwners,
  hasFreshSet,
  isConsultationOwner,
  isRenewalOwner,
  readIndex,
  rememberOwner
} from './answerSets'
import { usePatientStore } from './patient'

/** A stored set's answers, read without opening it. Empty when nothing is stored for the owner. */
export const readStoredAnswers = (owner: string): Pick<AnswersState, 'answers' | 'exitAnswers'> => {
  try {
    const stored = JSON.parse(safeStorage.getItem(answersStorageKey(owner)) || 'null')?.state

    if (stored?.owner !== owner) return { answers: {}, exitAnswers: {} }

    return { answers: stored.answers ?? {}, exitAnswers: stored.exitAnswers ?? {} }
  } catch {
    return { answers: {}, exitAnswers: {} }
  }
}

/** Whether the owner has answers saved within the last 30 days. */
export const hasSavedAnswers = (owner: string) => hasFreshSet(owner) && Object.keys(readStoredAnswers(owner).answers).length > 0

/** The in-memory set used when there is no consultation, as on a product page opened directly. */
const noConsultation = createStore<AnswersState>()((set, get) => ({ ...emptyAnswers(), ...answerActions(set, get) }))

/** One store per owner, kept across routes. */
const stores = new Map<string, AnswersStore>()

export const getAnswersStore = (owner: string): AnswersStore => {
  let store = stores.get(owner)

  if (!store) {
    store = createAnswersStore(owner)
    stores.set(owner, store)
  }

  return store
}

interface AnswersScope {
  store: StoreApi<AnswersState>
  /** Null when there is no consultation in progress and nothing else has taken the scope. */
  owner: string | null
  /** Taken by a flow's `AnswersScope`, rather than following the consultation in progress. */
  pinned: boolean
  hydrated: boolean
}

/** Which store is answering right now. */
export const useAnswersScope = create<AnswersScope>()(() => ({
  store: noConsultation,
  owner: null,
  pinned: false,
  hydrated: true
}))

const openScope = async (owner: string, pinned: boolean) => {
  const store = getAnswersStore(owner)

  rememberOwner(owner)

  useAnswersScope.setState({ store, owner, pinned, hydrated: store.persist.hasHydrated() })

  if (store.persist.hasHydrated()) return

  await store.persist.rehydrate()

  // The scope may have moved on while storage was being read.
  if (useAnswersScope.getState().owner === owner) useAnswersScope.setState({ hydrated: true })
}

/** Points an unpinned scope at the consultation `questionnaire_id` names. */
const followConsultation = () => {
  const scope = useAnswersScope.getState()
  if (scope.pinned) return

  const questionnaire = usePatientStore.getState().questionnaire_id

  if (!questionnaire) {
    useAnswersScope.setState({ store: noConsultation, owner: null, hydrated: true })
    return
  }

  const owner = consultationOwner(questionnaire)
  if (scope.owner !== owner) openScope(owner, false)
}

/** Reads every set in hand again after the patient store reloads, which embedded is when the host page's storage arrives. */
const rehydrateAnswerSets = () => {
  // The host's index predates this page, so the set in use is indexed again.
  const { owner } = useAnswersScope.getState()
  if (owner) rememberOwner(owner)

  for (const store of stores.values()) store.persist.rehydrate()
  followConsultation()
}

usePatientStore.subscribe((state, previous) => {
  if (state.questionnaire_id !== previous.questionnaire_id) followConsultation()
})
usePatientStore.persist.onFinishHydration(rehydrateAnswerSets)
followConsultation()

/** Pins the scope to one owner's answers. Hydration is reported through the scope rather than awaited. */
export const enterAnswersScope = (owner: string) => openScope(owner, true)

/** Hand the answers back to the consultation in progress. */
export const leaveAnswersScope = () => {
  useAnswersScope.setState({ pinned: false })
  followConsultation()
}

/** Empties the matching sets, in hand or only in storage, keeping the instances the pages hold. */
const clearAnswerSets = (matches: (owner: string) => boolean) => {
  const owners = [...new Set([...stores.keys(), ...readIndex().map(entry => entry.owner)])].filter(matches)

  for (const owner of owners) stores.get(owner)?.getState().reset()
  forgetOwners(owners)
}

/** Drop a submitted set so the next one starts clean, keeping the instance the pages hold. */
export const clearAnswersStore = (owner: string) => {
  stores.get(owner)?.getState().reset()
  forgetOwners([owner])
}

/** Clears the sets of the consultations a purchase completed. Other consultations in progress keep their answers. */
export const clearConsultationAnswers = (questionnaireHashes: (string | null | undefined)[]) => {
  const owners = new Set(questionnaireHashes.filter(Boolean).map(consultationOwner))

  clearAnswerSets(owner => owners.has(owner))
}

/** Clears consultation and renewal sets and the in-memory set, on a logout or a user switch. Request sets are kept. */
export const clearPatientAnswers = () => {
  clearAnswerSets(owner => isConsultationOwner(owner) || isRenewalOwner(owner))
  noConsultation.setState(emptyAnswers())
}

/** Opens a set outside its pages, read back from storage before anything is written to it. */
export const loadAnswerSet = async (owner: string): Promise<AnswersStore> => {
  const store = getAnswersStore(owner)

  rememberOwner(owner)
  if (!store.persist.hasHydrated()) await store.persist.rehydrate()

  return store
}

/** A set we opened: still in hand, or still indexed after a reload. */
const isTrackedOwner = (owner: string) => stores.has(owner) || readIndex().some(entry => entry.owner === owner)

/**
 * Deletes an answer from a named set, since the exit page is its own route. An owner nothing opened is ignored,
 * and the set is read back first so a reload does not persist an empty set.
 */
export const deleteAnswerFor = async (owner: string | null | undefined, questionHash: string) => {
  if (!owner) return answersState().deleteAnswer(questionHash)
  if (!isTrackedOwner(owner)) return

  const store = await loadAnswerSet(owner)

  store.getState().deleteAnswer(questionHash)
}

export const useAnswers = () => useStore(useAnswersScope(scope => scope.store))

export const useAnswersSlice = <T>(selector: (state: AnswersState) => T) =>
  useStore(
    useAnswersScope(scope => scope.store),
    selector
  )

/** The current answers outside render. */
export const answersState = () => useAnswersScope.getState().store.getState()
